/**
 * Use Term API
 *
 * API endpoints for use term management operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import { saveTermsAuthorizationArgsSchema } from "./useTerm.schema";
import type { GetTermsByCompanyArgs, SaveTermsAuthorizationArgs, Term } from "./useTerm.types";

/**
 * Use Term API endpoints
 * Provides operations for use term management
 */
export const useTermApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get terms by company ID
     * @param id - Company ID
     * @returns Array of terms
     */
    getTermsByCompany: build.query<Term[], GetTermsByCompanyArgs>({
      query: ({ id }) => `/useTerm/company/${id}`,
      transformResponse: (response: Term[]) => {
        return response.map((term) => ({
          ...term,
          createdAt: dayjs(term.createdAt).format("DD/MM/YYYY - HH:mm"),
          updatedAt: term.updatedAt ? dayjs(term.updatedAt).format("DD/MM/YYYY - HH:mm") : null
        }));
      }
    }),

    /**
     * Get single term user by ID
     * @param id - Term ID
     * @returns Term details
     */
    getTermsUserByCompany: build.query<Term, Pick<Term, "id">>({
      query: ({ id }) => `/useTerm/user/company/${id}`
    }),

    /**
     * Save Terms Authorization
     * Sends a request to save authorization information for use terms, including which communication channels (WhatsApp, SMS, Email) are enabled,
     * the associated term/user/driver/contact data. This is typically used to record that a user or driver has accepted or been notified
     * about the terms through various methods.
     *
     * @param body - The payload containing authorization options and links to user/driver/contact data.
     * @see SaveTermsAuthorizationArgs for shape of the payload.
     * @see saveTermsAuthorizationArgsSchema for schema validation.
     */
    saveTermsAuthorization: build.mutation<void, SaveTermsAuthorizationArgs>({
      query: (body) => ({
        url: "/useTerm/authorization/save",
        method: "POST",
        body
      }),
      argSchema: saveTermsAuthorizationArgsSchema,
      skipSchemaValidation: true
    })
  })
});

useTermApi.enhanceEndpoints({
  addTagTypes: ["UseTerm", "UseTermByCompany", "UseTermUserByCompany"],
  endpoints: {
    getTermsByCompany: {
      providesTags: (result, error, arg) => [{ type: "UseTermByCompany", id: arg.id }],
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando termos...",
          success: "Termos carregados com sucesso!",
          error: "Erro ao carregar termos."
        });
      }
    },
    getTermsUserByCompany: {
      providesTags: (result, error, { id }) => [{ type: "UseTermUserByCompany", id }]
    },
    saveTermsAuthorization: {
      invalidatesTags: ["UseTerm", "UseTermByCompany", "UseTermUserByCompany"]
    }
  }
});
